<script setup>
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  records: {
    type: Array,
    default: () => [],
  },
  knowledgebaseId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['upload', 'delete']);

const router = useRouter();
const { t } = useI18n();

const navigateToKnowledgebase = () => {
  if (props.knowledgebaseId) {
    router.push({
      name: 'settings_knowledgebases_content',
      params: { knowledgebaseId: props.knowledgebaseId },
    });
  }
};

const handleDelete = record => {
  emit('delete', record);
};
</script>

<template>
  <div className="h-full flex flex-col">
    <div class="flex items-center justify-end mb-6">
      <Button
        icon="i-lucide-upload"
        variant="ghost"
        size="sm"
        @click="emit('upload')"
      >
        {{ t('SETTINGS.SPACES.DOCUMENTS.UPLOAD') }}
      </Button>
      <Button
        icon="i-lucide-external-link"
        variant="ghost"
        size="sm"
        @click="navigateToKnowledgebase"
      >
        {{ t('SETTINGS.SPACES.DOCUMENTS.VIEW_ALL') }}
      </Button>
    </div>

    <div
      v-if="records.length === 0"
      class="py-8 text-center flex flex-col w-full h-full justify-center items-center"
    >
      <Icon
        icon="i-lucide-file-x"
        class="size-12 mx-auto text-woot-75 dark:text-woot-200/25 mb-2"
      />
      <p class="text-sm text-slate-500 font-semibold !mb-0">
        {{ t('SETTINGS.SPACES.DOCUMENTS.EMPTY') }}
      </p>
    </div>

    <div v-else class="space-y-2 max-h-[320px] overflow-y-auto">
      <div
        v-for="record in records.slice(0, 10)"
        :key="record.id"
        class="group flex items-center gap-3 p-3 rounded-lg border border-slate-75 dark:border-slate-800 hover:border-woot-100 dark:hover:border-woot-700 hover:shadow-sm"
      >
        <Icon icon="i-lucide-file" class="size-5 text-slate-200" />
        <div
          class="flex-1 min-w-0 cursor-pointer"
          @click="navigateToKnowledgebase"
        >
          <p
            class="text-sm font-medium text-slate-900 dark:text-white truncate !mb-0"
          >
            {{ record.name || t('SETTINGS.SPACES.DOCUMENTS.UNTITLED') }}
          </p>
        </div>
        <button
          class="flex opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all w-10 h-10 justify-center items-center"
          :title="t('SETTINGS.SPACES.DOCUMENTS.DELETE')"
          @click.stop="handleDelete(record)"
        >
          <Icon icon="i-lucide-trash-2" class="size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
